/**
 * The conversation column.
 *
 * Scrolling follows the answer only while the visitor is already at the bottom
 * — yanking the view back while somebody is re-reading an earlier reply is the
 * same theft of attention as switching their tab for them.
 *
 * **The activity feed vs. `ThinkingLine`** (§5.4). The canvas graph's own
 * progress feed replaces `ThinkingLine` — but only once it has actually said
 * something: `?graph=peterhof_agent|peterhof_guide` never emit an `activity`
 * frame at all, so their feed (created empty by `activityStore.begin()` at
 * every turn, regardless of graph) never gains `hasFrames`, and they stay on
 * `ThinkingLine` exactly as before. A FINISHED feed is drawn right after the
 * bubble it is anchored to (`anchorId`, set once by `finish()`), which is the
 * one thing that survives an F5 — `turn` numbers do not (`chatStore.restore()`
 * stamps every restored bubble `turn: 0`). The LIVE feed has no anchor and
 * always renders at the very bottom, the slot `ThinkingLine` used to hold —
 * gated on `chat.status`, not on the feed's own `finished` flag, so a feed
 * that the backend's `finished` frame already touched (before `endTurn()` has
 * run) does not flicker out from under a still-streaming turn.
 *
 * **`ReasoningBlock` sits directly above `ActivityFeed`, same anchor, same
 * turn** (reasoning-summary design doc §10.4). It is gated purely on its OWN
 * `hasReasoningFrames` — never on whether the activity feed exists — so a
 * tool-free turn that still reasoned (or, symmetrically, a graph that has
 * tools but no `PC_SHOW_REASONING`) draws exactly the block it has frames
 * for. Read by `chatTurn`, not `liveFeed.turn`: a reasoning frame can land on
 * a turn that never gets an activity frame at all.
 *
 * **`peterhof_orch`'s NARRATOR lines are grouped, not listed** (`progressGroups
 * .ts`). They arrive as ordinary `pa_bridge` bubbles and would otherwise stack
 * under the `say` handoff one per event; instead a run of them belonging to one
 * `delegate` call is drawn as a single item — open while the job is running,
 * folded into «Что делал помощник · N» once the turn's answer stands.
 *
 * **`peterhof_orch` has neither, and draws the turn's cards instead**
 * (`sessionStore.showsActivityFeed` / `showsTurnCards`, user decision
 * 2026-09-11). No feed live, no collapsed summary, no reasoning block — one
 * neutral `ThinkingLine` while the turn runs — and `TurnCards` right after the
 * bubble the graph put the cards on. Every other graph renders exactly as
 * above.
 */

import { Fragment, useEffect, useMemo, useRef } from 'react';
import { useChat } from '../store/chatStore';
import { hasNoBoard, showsActivityFeed, useSession } from '../store/sessionStore';
import { useActivity } from '../activity/activityStore';
import ActivityFeed from '../activity/ActivityFeed';
import ReasoningBlock from '../activity/ReasoningBlock';
import Bubble from './Bubble';
import Chips from './Chips';
import ProgressGroup from './ProgressGroup';
import ThinkingLine from './ThinkingLine';
import TurnCards from './TurnCards';
import { cardsUnder, latestSnapshots } from './inlineTurnCards';
import { threadItems } from './progressGroups';
import { Starters } from './Chips';
import QuestionCard from './QuestionCard';
import { useQuestionnaire, type ResumeValue } from '../store/questionnaireStore';
import phrases from '../lib/generated/phrases.json';
import AgentRoutePane from '../location/AgentRoutePane';
import { useNavigation } from '../location/navigationStore';
import type { Bubble as BubbleModel } from '../store/chatStore';

/**
 * The board-less graph's first screen (#164, owner 29.09.2026 — revises US-1.1 #23): the
 * greeting and one row of start buttons, nothing else. «Всё самое главное» (#154) is the
 * row's first button, drawn like the rest; its `starter` goes with its line (`tapStarter`).
 * Every word is the graph's (`graphs/peterhof_plan/first_screen.py` → `phrases.agent`); the
 * other buttons are the shipped chips on topics the graph has data for.
 */
function AgentFirstScreen({ sendDisabled }: { sendDisabled: boolean }) {
  const screen = phrases.agent;
  return (
    <div className="greeting">
      {/* The Figma intro art (1-2 «Chat - Intro», 07.10.2026) — decoration, not
          a block: INV-PH-60's «nothing else» is about content, and the owner
          confirmed the picture. */}
      <div className="greeting__art" aria-hidden="true">
        <picture>
          <source srcSet="/figma/splash.webp" type="image/webp" />
          <img src="/figma/splash.jpg" alt="" width="1200" height="741" />
        </picture>
      </div>
      <h2 className="greeting__title">Здравствуйте!</h2>
      <p className="greeting__text">{screen.greeting}</p>
      <Starters chips={screen.starters} disabled={sendDisabled} />
    </div>
  );
}

/**
 * The empty thread. A board-less graph (`hasNoBoard`) has its own first
 * screen (#132, #23) — the canvas family's line promises a board that graph
 * does not have.
 */
function Greeting({ assistantId, sendDisabled }: { assistantId: string; sendDisabled: boolean }) {
  if (hasNoBoard(assistantId)) return <AgentFirstScreen sendDisabled={sendDisabled} />;
  return (
    <div className="greeting">
      <h2 className="greeting__title">Здравствуйте!</h2>
      <p className="greeting__text">
        Я гид Петергофа. Спрашивайте про дворцы, фонтаны, билеты и дорогу — а план поездки я буду
        собирать на доске рядом.
      </p>
      <Starters disabled={sendDisabled} />
    </div>
  );
}

/** The last message of the turn that built the route; `null` while that turn
 *  has none on screen yet (the card then waits at the bottom). */
function routeAnchorIn(bubbles: BubbleModel[], turn: number | null): string | null {
  if (turn === null) return null;
  for (let i = bubbles.length - 1; i >= 0; i -= 1) if (bubbles[i]!.turn === turn) return bubbles[i]!.id;
  return null;
}

export default function Thread({
  sendDisabled = false,
  onQuestionnaireComplete,
  onQuestionnaireExit,
}: {
  sendDisabled?: boolean;
  onQuestionnaireComplete: (resume: ResumeValue, summary: string) => void;
  onQuestionnaireExit: (text: string, answered?: Record<string, string[] | string>, skipped?: string[]) => void;
}) {
  const bubbles = useChat((state) => state.bubbles);
  const error = useChat((state) => state.error);
  const clearError = useChat((state) => state.clearError);
  const setDraft = useChat((state) => state.setDraft);
  const chatStatus = useChat((state) => state.status);
  const chatTurn = useChat((state) => state.turn);
  const feeds = useActivity((state) => state.feeds);
  const assistantId = useSession((state) => state.assistantId);
  const questionnaireSlot = useQuestionnaire((state) => state.active?.currentSlot);
  const questionnaireActive = questionnaireSlot !== undefined;
  const feedShown = showsActivityFeed(assistantId);
  // The Agent's answers carry the design's «Ответ» over the first reply of a
  // turn (Figma «Text-Answer»); the other graphs' chats are drawn as before.
  const labelsAnswers = hasNoBoard(assistantId);
  // The dev Agent's route (INV-PH-66): drawn under the last message of the
  // turn whose run built it — `AgentRoutePane` itself decides whether there is
  // anything to draw (dev build, this graph, a current route).
  const routeTurn = useNavigation((state) => (state.preview ? state.turn : null));
  // …or, after a reload (B13), under the bubble it was kept against: turn numbers restart, message ids do not.
  // A card whose answer is not on screen is not drawn at the bottom — it was a card of that answer, not of the last.
  const restoredAnchor = useNavigation((state) => (state.preview && state.restored ? state.anchorId : null));
  const routeAnchor = restoredAnchor !== null
    ? (bubbles.some((bubble) => bubble.id === restoredAnchor) ? restoredAnchor : null)
    : routeAnchorIn(bubbles, routeTurn);
  const scroller = useRef<HTMLDivElement>(null);
  const pinned = useRef(true);

  const lastUser = useRef<string | null>(null);

  useEffect(() => {
    const node = scroller.current;
    // The visitor's own new line brings the chat back to its end, wherever they had scrolled: a question sent
    // from the scrollback left its answer below the fold (first-user QA, dev, 21.10.2026).
    const last = bubbles[bubbles.length - 1];
    if (last?.role === 'user' && last.id !== lastUser.current) {
      lastUser.current = last.id;
      pinned.current = true;
    }
    if (!node || !pinned.current) return;
    // An empty thread is the first screen, read from its top (#23).
    node.scrollTop = bubbles.length === 0 ? 0 : node.scrollHeight;
  }, [bubbles, error, questionnaireSlot, routeTurn, restoredAnchor]);

  useEffect(() => {
    const node = scroller.current;
    if (!node) return;
    // A card's photographs load after the card is drawn and push the end of the chat down; a visitor reading
    // the end stays at it. `load` does not bubble — caught on the way down.
    const keep = (event: Event) => {
      if (pinned.current && bubbles.length > 0 && event.target instanceof HTMLImageElement) {
        node.scrollTop = node.scrollHeight;
      }
    };
    node.addEventListener('load', keep, true);
    return () => node.removeEventListener('load', keep, true);
  }, [bubbles.length]);

  // The live feed is whichever one belongs to the turn in flight — read by
  // TURN, not by `finished`, so the backend's own `finished` frame (which can
  // land microtasks before `endTurn()` does) cannot make it vanish early.
  const liveFeed = chatStatus !== 'idle' ? feeds.find((f) => f.turn === chatTurn) : undefined;
  const showLiveFeed = feedShown && liveFeed !== undefined && liveFeed.hasFrames;
  // Same turn, but its own gate (`ReasoningBlock` reads `hasReasoningFrames`
  // itself) — a turn in flight may have reasoning frames with no activity
  // ones yet, or the other way round.
  const liveReasoningTurn = feedShown && chatStatus !== 'idle' ? chatTurn : undefined;

  // Board-less: which answer holds the NEWEST snapshot of each card id — only
  // that one is editable (`✕` on a stop, card actions); older ones are read-only.
  const latest = useMemo(
    () => (hasNoBoard(assistantId) ? latestSnapshots(bubbles, assistantId) : undefined),
    [bubbles, assistantId],
  );

  const anchoredTurn = new Map<string, number>();
  for (const feed of feeds) {
    if (feed.finished && feed.anchorId) anchoredTurn.set(feed.anchorId, feed.turn);
  }

  return (
    <div
      className="thread"
      ref={scroller}
      onScroll={(event) => {
        const node = event.currentTarget;
        pinned.current = node.scrollHeight - node.scrollTop - node.clientHeight < 80;
      }}
    >
      {bubbles.length === 0 ? <Greeting assistantId={assistantId} sendDisabled={sendDisabled} /> : null}
      {threadItems(bubbles).map((item, index, items) => {
        // `peterhof_orch`'s narrator lines travel as ordinary bubbles and are
        // drawn as ONE group under the `say` handoff they belong to — open while
        // the job runs, folded into a summary once the answer arrives
        // (`progressGroups.ts`). Every other bubble is unchanged.
        if (item.kind === 'progress') return <ProgressGroup group={item} key={item.key} />;
        const { bubble } = item;
        const anchored = feedShown ? anchoredTurn.get(bubble.id) : undefined;
        const cards = cardsUnder(bubble, assistantId);
        const before = index > 0 ? items[index - 1] : undefined;
        const opensAnswer =
          labelsAnswers &&
          bubble.role === 'guide' &&
          !bubble.bridge &&
          (before === undefined || (before.kind !== 'progress' && before.bubble.role === 'user'));
        return (
          <Fragment key={bubble.id}>
            <Bubble bubble={bubble} label={opensAnswer ? 'Ответ' : undefined} />
            {cards.length > 0 ? (
              <TurnCards cards={cards} assistantId={assistantId} latest={latest} bubbleId={bubble.id} />
            ) : null}
            {anchored !== undefined ? (
              <>
                <ReasoningBlock turn={anchored} />
                <ActivityFeed turn={anchored} />
              </>
            ) : null}
            {bubble.id === routeAnchor ? <AgentRoutePane /> : null}
          </Fragment>
        );
      })}
      {routeTurn !== null && routeAnchor === null ? <AgentRoutePane /> : null}
      {liveReasoningTurn !== undefined ? <ReasoningBlock turn={liveReasoningTurn} /> : null}
      {showLiveFeed ? <ActivityFeed turn={liveFeed!.turn} /> : <ThinkingLine neutral={!feedShown} />}
      {questionnaireActive ? (
        <QuestionCard onComplete={onQuestionnaireComplete} onExit={onQuestionnaireExit} />
      ) : (
        <Chips />
      )}
      {error ? (
        <div className="error" role="alert">
          <p className="error__text">{error.text}</p>
          {error.retryText ? (
            <button
              type="button"
              className="btn"
              onClick={() => {
                // The utterance goes back into the composer rather than being
                // re-sent: a failed turn may have cost tokens, and a re-send the
                // visitor did not ask for is a second one.
                setDraft(error.retryText!);
                clearError();
              }}
            >
              Повторить
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
